import _ from "lodash";
import type { SlaClock } from "@ServerTypes/sla/sla.schema";
import { isMonthKey, monthRangeMs } from "@Helpers";
import { convertDate } from "@Services/luxonHelper";
import { jiraController } from "@Features/jira/api/jira.api";
import { formatDuration } from "./useSlaCountdown";

/**
 * One SLA clock as a report reads it, or `null` when the ticket has none. `SlaClock` is the server's
 * own record (sla/sla.schema.ts) — the same type `jiraTicketListRow.fields.sla` is built from — so
 * this is no longer a hand-declared subset of it; `state` in particular is now the six-way literal
 * union rather than a bare `string`.
 */
export type SlaReportClock = SlaClock | null;

export type SlaVerdict = "met" | "breached" | "pending" | "paused" | "na";

/**
 * One support ticket as `POST /jira/getTicketsList` returns it.
 *
 * ⚠️ DECLARED HERE, not derived from the route's response contract. On the staging line this is a
 * nested `Pick` of `jiraTicketListRow` (jira/jira.schema.ts), but that contract is part of a
 * separate piece of work and does not exist on this branch — `jiraControllerResponse` is still the
 * opaque `statusResponse`, so no row shape is declared server-side. These are the fields the two SLA
 * reports read, all of them through `_.get`. When the list contract lands here, replace this with
 * the `Pick` so the two branches converge again.
 *
 * `fields.sla` is present only on SLA-tracked (bug) tickets, and `fields.severity` only on portals
 * running the CUSTOM_SUPPORT_SLA model. Everything is optional because `jiraTicketDetails.tsx`
 * intersects this type for the details route and seeds its state with `{}` before the first fetch.
 */
export type SlaReportTicket = {
  _id?: string;
  jiraTicketKey?: string;
  createdOn?: number;
  portal?: { name?: string; customSupportSla?: boolean; frontendUrl?: string };
  fields?: {
    sla?: {
      priorityId?: string;
      response?: SlaReportClock;
      resolution?: SlaReportClock;
    };
    priority?: { id?: string; label?: string };
    severity?: { id?: string; label?: string };
    issueType?: { label?: string };
    impact?: { label?: string };
    issueStatus?: { label?: string };
  };
};

export const clockOf = (ticket: SlaReportTicket, variant: "response" | "resolution"): SlaReportClock =>
  _.get(ticket, `fields.sla.${variant}`) ?? null;

export const verdictOf = (clock: SlaReportClock): SlaVerdict => {
  if (!clock) return "na";
  if (clock.actualAt != null) return clock.metInTime ? "met" : "breached";
  if (clock.state === "breachedOpen") return "breached";
  if (clock.state === "paused") return "paused";
  return "pending";
};

export type VerdictCounts = { met: number; breached: number; pending: number; paused: number };
export const emptyVerdictCounts = (): VerdictCounts => ({ met: 0, breached: 0, pending: 0, paused: 0 });

export const countVerdict = (counts: VerdictCounts, verdict: SlaVerdict): void => {
  if (verdict !== "na") counts[verdict] += 1;
};

export const compliancePct = (counts: VerdictCounts): number | null => {
  const decided = counts.met + counts.breached;
  return decided === 0 ? null : Math.round((counts.met / decided) * 100);
};

export const pctLabel = (counts: VerdictCounts): string => {
  const value = compliancePct(counts);
  return value == null ? "-" : `${value}%`;
};

export const ticketsInScope = (counts: VerdictCounts): number =>
  counts.met + counts.breached + counts.pending + counts.paused;

export const avg = (arr: number[]): number | null => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

export const durationLabel = (average: number | null): string => (average != null ? formatDuration(average) : "-");

export const instant = (ms: number | null | undefined): string =>
  ms ? String(convertDate(ms, "dateForPdfExport")) : "-";

/**
 * What one SLA report covers. The month is the whole scope — the export modal decides it, and the
 * ticket grid's own column filters play no part.
 *
 * ⚠️ THE TWO HALVES ARE SCOPED BY DIFFERENT THINGS, because they are filed by different things.
 * `portalFrontendUrl` scopes the INCIDENT half: the tickets are matched on hostname rather than
 * `portal.name` because an on-premise portal's tickets carry the name from that deployment's own
 * portal document, which need not match the cloud one (BPCL's cloud document is named "BPCL") —
 * the hostname is the same in both. `tenant` scopes the INFRASTRUCTURE half: the availability
 * generator files its months under a deployment folder (`cube` | `mp` | `bpcl`), which is not a
 * portal and has no hostname. {@link SLA_REPORT_PORTALS} is where one picker produces both.
 */
export type SlaReportScope = {
  createdBy: string;
  month?: string;
  portalFrontendUrl?: string;
};

const REPORT_PAGE_SIZE = 100000;

export function readSlaReportScope(search: string): SlaReportScope {
  const params = new URLSearchParams(search);
  const createdBy = params.get("supportCreatedBy") || "createdByAnyPortalUsers";
  const month = params.get("supportMonth");
  const portalFrontendUrl = params.get("supportPortalUrl");
  return {
    createdBy,
    ...(isMonthKey(month) ? { month } : {}),
    ...(portalFrontendUrl ? { portalFrontendUrl } : {}),
  };
}

/** `createdOn` is a `useDateFilter` field server-side, so the bounds are epoch-ms and inclusive. */
export function slaReportWhere({ month, portalFrontendUrl }: SlaReportScope): unknown[] {
  const predicates: unknown[] = [];
  const range = month ? monthRangeMs(month) : null;
  if (range) {
    predicates.push(
      { field: "createdOn", operator: "greaterthanorequal", value: range.startMs },
      { field: "createdOn", operator: "lessthanorequal", value: range.endMs },
    );
  }
  if (portalFrontendUrl) {
    predicates.push({ field: "portal.frontendUrl", operator: "equal", value: portalFrontendUrl });
  }
  return predicates;
}

export async function fetchSlaReportTickets(scope: SlaReportScope): Promise<SlaReportTicket[]> {
  const where = slaReportWhere(scope);
  const res = await jiraController({
    value: {
      action: "getList",
      createdBy: scope.createdBy,
      requiresCounts: false,
      take: REPORT_PAGE_SIZE,
      ...(where.length ? { where } : {}),
    },
  });
  // `jiraController` is the shared multi-action helper, so axios types its body with the opaque
  // `jiraControllerResponse` every jira route registers on this branch — it declares no `result`, so
  // the row shape has to be asserted here. It is an ASSERTION, not a checked narrowing: nothing
  // server-side guarantees these members yet. Everything downstream reads them through `_.get` and
  // treats each as optional, which is why that is survivable. It stops being an assertion the day
  // `/getTicketsList` registers its own response contract (see the note on `SlaReportTicket`).
  const body = res.data as { status?: boolean; result?: SlaReportTicket[] };
  if (!body.status) return [];
  // Still optional after narrowing: the success arm declares `result` optional, and a scope that
  // matches nothing comes back without it.
  return body.result ?? [];
}

/**
 * Which of the two SLA models the report should be printed in.
 *
 * ⚠️ THE "MIXED MODELS" CASE IS GONE, DELIBERATELY. It used to be possible — and to be refused with
 * a toast — because the export took whatever the grid's filters happened to select, which could
 * span a severity-only portal and standard impact/priority portals at once. The export now names
 * exactly ONE portal (the picker for a whitelisted user, the caller's own for everyone else), and a
 * portal has exactly one model, so a mixed set is unreachable rather than merely unlikely. Nothing
 * refuses an export over this any more.
 *
 * `portal.customSupportSla` is the flag; the first ticket decides, since they all share a portal. A
 * month with no tickets never reaches here — the report prints its own empty-month notice.
 */
export const slaReportUsesSeverityModel = (tickets: SlaReportTicket[]): boolean =>
  tickets.some((tk) => _.get(tk, "portal.customSupportSla"));

/** One row of the SLA export's portal picker. */
export type SlaReportPortalOption = { frontendUrl: string; label: string };

/**
 * The portals a whitelisted exporter may pull the report for — every portal there is, derived from
 * the create-on-behalf-of user list the page has already fetched rather than from a second request
 * or a hand-maintained array.
 *
 * Deduplicated on `frontendUrl` because that list is per USER: 1,250 users across ~974 portals push
 * the same portal once per member. The hostname is the key for the same reason it scopes the ticket
 * query — an on-premise portal's document may be named differently from its cloud counterpart, and
 * the label carries both so two portals that share a name stay distinguishable.
 *
 * Rows with no hostname are dropped: a portal that cannot be named in `portal.frontendUrl` cannot
 * scope anything.
 */

/**
 * How a portal is labelled in a picker: `Name - hostname`.
 *
 * The hostname belongs in the label, not as decoration: two portals can share a `name` (an
 * on-premise portal's document may be named differently from its cloud counterpart), and the
 * hostname is what both the SLA export and the ticket query actually scope on. Shared so the SLA
 * export modal and the create-on-behalf-of picker cannot drift apart.
 */
export const portalPickerLabel = (portal: { name?: string; frontendUrl?: string }): string =>
  portal.name && portal.frontendUrl
    ? `${portal.name} - ${portal.frontendUrl}`
    : (portal.frontendUrl ?? portal.name ?? "");

export function slaReportPortalOptions(
  users: { portals: { name: string; frontendUrl: string }[] }[],
): SlaReportPortalOption[] {
  const byHost = new Map<string, SlaReportPortalOption>();
  for (const user of users) {
    for (const portal of user.portals) {
      if (!portal.frontendUrl || byHost.has(portal.frontendUrl)) continue;
      byHost.set(portal.frontendUrl, {
        frontendUrl: portal.frontendUrl,
        label: portalPickerLabel(portal),
      });
    }
  }
  return [...byHost.values()].sort((a, b) => a.label.localeCompare(b.label));
}
